import React, { useRef, useState, useEffect, useLayoutEffect } from 'react';
import ReactDOM from 'react-dom';
import { useConversation } from '@elevenlabs/react';
// import css from './css/ChatWidget.css?inline';
import Linkify from 'react-linkify';
import axios from 'axios';
import { CommentOutlined, CommentFilled, HomeFilled, SettingFilled, ReconciliationOutlined, RightOutlined } from '@ant-design/icons';
import LanguageSelect from "./LanguageSelect";

// ShadowWrapper component to encapsulate with Shadow DOM
const ShadowWrapper = ({ children, themeColor }) => {
  const shadowHostRef = useRef(null);
  const shadowRootRef = useRef(null);

  useEffect(() => {
    if (shadowHostRef.current && !shadowRootRef.current) {
      // Create shadow root
      const shadowRoot = shadowHostRef.current.attachShadow({ mode: 'open' });
      shadowRootRef.current = shadowRoot;

      // Inject CSS into shadow DOM
      const styleElement = document.createElement('style');
      styleElement.textContent = getChatWidgetCSS();
      shadowRoot.appendChild(styleElement);

      // Create container for React content
      const container = document.createElement('div');
      container.id = 'chat-widget-root';
      shadowRoot.appendChild(container);

      // Set CSS variables within shadow root
      if (themeColor) {
        const hex = themeColor.replace('#', '');
        const r = parseInt(hex.substr(0, 2), 16);
        const g = parseInt(hex.substr(2, 2), 16);
        const b = parseInt(hex.substr(4, 2), 16);

        const lightR = Math.min(255, r + 40);
        const lightG = Math.min(255, g + 40);
        const lightB = Math.min(255, b + 40);
        const darkR = Math.max(0, r - 40);
        const darkG = Math.max(0, g - 40);
        const darkB = Math.max(0, b - 40);

        shadowRoot.host.style.setProperty('--theme-color', themeColor);
        shadowRoot.host.style.setProperty('--theme-color-light', `rgb(${lightR}, ${lightG}, ${lightB})`);
        shadowRoot.host.style.setProperty('--theme-color-dark', `rgb(${darkR}, ${darkG}, ${darkB})`);
        shadowRoot.host.style.setProperty('--theme-color-transparent', `rgba(${r}, ${g}, ${b}, 0.1)`);
      }
      shadowRoot.host.style.setProperty(`display`, `block`, 'important');
    }
  }, [themeColor]);

  // Function to get all CSS content
  const getChatWidgetCSS = () => {
    return `
      :host {
        all: initial;
        display: block;
      }

      /* Import variables CSS */
      :host {
        --font-primary: "Nunito Sans", sans-serif;
        --font-weight-light: 300;
        --font-weight-regular: 400;
        --font-weight-medium: 500;
        --font-weight-semibold: 600;
        --font-weight-bold: 700;
        --color-primary: #000000;
        --color-primary-light: #333333;
        --color-primary-dark: #000000;
        --color-secondary: #666666;
        --color-accent: #444444;
        --color-background: #ffffff;
        --color-surface: #f5f5f5;
        --color-border: #e0e0e0;
        --color-text: #1a1a1a;
        --color-text-muted: #666666;
        --color-success: #2d2d2d;
        --color-warning: #4a4a4a;
        --color-error: #333333;
        --color-info: #555555;
        --theme-color: #d67900;
        --theme-color-light: #e68a1a;
        --theme-color-dark: #b36600;
        --theme-color-transparent: rgba(214, 121, 0, 0.1);
      }

      .mt-0 {
        margin-top: 0!important;
      }
      .mb-0 {
        margin-bottom: 0!important;
      }

      body {
          font-family: var(--font-primary);
          font-weight: 400!important;
      }

      /* ChatWidget Container */
      .chat-widget-container {
        font-family: var(--font-primary)!important;
        position: fixed;
        bottom: 15px;
        right: 15px;
        z-index: 1000;
        text-transform:none;
      }

      /* Position classes for dynamic positioning */
      .chat-widget-container.position-bottom-right {
        bottom: 20px;
        right: 20px;
      }

      .chat-widget-container.position-bottom-left {
        bottom: 20px;
        left: 20px;
      }

      .chat-widget-container.position-top-right {
        top: 20px;
        right: 20px;
      }

      .chat-widget-container.position-top-left {
        top: 20px;
        left: 20px;
      }

      /* Floating Button - Matching the screenshot design exactly */
      .widget-float-button {
        background: transparent;
        border: none;
        cursor: pointer;
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 0;
        display: flex !important;
        visibility: visible !important;
        opacity: 1 !important;
      }

      .widget-float-button:hover {
        transform: translateY(-1px);
      }

      .widget-float-button:active {
        transform: translateY(0);
      }

      /* Button content container */
      .button-content {
        display: flex;
        align-items: center;
        gap: 8px;
      }

      /* Icon circle with gradient background */
      .icon-circle {
        width: 36px;
        height: 36px;
        background: linear-gradient(135deg, #333333 0%, #666666 100%);
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
        transition: all 0.3s ease;
        overflow: hidden;
      }

      .icon-circle img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        border-radius: 50%;
      }

      .icon-circle svg {
        width: 20px;
        height: 20px;
        fill: none;
        stroke: white;
        stroke-width: 2;
      }

      /* System status indicator */
      .system-status {
        background: var(--color-surface);
        border: 1px solid var(--color-border);
        border-radius: 8px;
        padding: 12px;
        margin: 10px 0;
        text-align: center;
      }

      .status-indicator {
        color: var(--color-text-muted);
        font-size: 14px;
        font-weight: 500;
      }

      .status-details {
        margin-top: 5px;
        color: var(--color-text-muted);
        font-size: 12px;
      }
      .call-icon {
        position: absolute;
        left: 0;
        top: 50%;
        transform: translateY(-50%);
        z-index: -1;
      }

      /* Pill-shaped call button */
      .call-button {
              position: relative;
        display: inline-flex;
        align-items: center;
        background: var(--theme-color);
        color: white;
        padding: 10px 7px;
        border-radius: 25px;
        font-size: 14px;
        font-weight: 500;
        white-space: nowrap;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
        text-transform: lowercase;
        letter-spacing: -4px;
        writing-mode: vertical-rl;
        text-orientation: upright;
      }

      .widget-float-button:hover .icon-circle {
        box-shadow: 0 6px 20px rgba(0, 0, 0, 0.4);
      }

      .widget-float-button:hover .call-button {
        background: var(--theme-color-light);
      }



      .chat-widget-call-btn {
        position: relative;
        right: 0px;
        bottom: 180px;
        padding: 4px;
        background: white;
        border-radius: 50px;
        --tw-shadow: 0 2px 24px 1px rgba(0, 0, 0, .16);
        --tw-shadow-colored: 0 2px 24px 1px var(--tw-shadow-color);
        box-shadow: var(--tw-ring-offset-shadow, 0 0 #0000), var(--tw-ring-shadow, 0 0 #0000), var(--tw-shadow);
      }

      .chat-widget-container.bubble-open {
        bottom: 150px;
      }

      .chat-widget-container.bubble-open .chat-widget-call-btn {
        transform: rotate(0deg);
        right: 0px;
        bottom: 10px;
      }

      /* Widget Panel - Modern card design */
      .widget-panel {
        overflow-y: auto;
        position: absolute;
        bottom: 80px !important;
        right: 0 !important;
        width: 380px !important;
        height: 440px;
        height: 520px;
        padding: 0 0 10px 0;
        background: #f8f8f8;
        border-radius: 20px;
        box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
        display: flex;
        flex-direction: column;
        overflow: hidden;
        border: 1px solid rgba(255, 255, 255, 0.2);
        display: flex !important;
        visibility: visible !important;
        opacity: 1 !important;
        box-sizing: revert;
      }
      .widget-panel-middle-scroll-area {
        height: 473px;
        overflow-y: auto;
      }
      .widget-panel-middle-scroll-area.chat-area-zena {
          height: 343px;
          padding: 58px 0 0 0;
      }
      /* Panel Header */
      .widget-header {
        background: linear-gradient(135deg, var(--theme-color) 0%, var(--theme-color-dark) 100%);
        color: white;
        padding: 20px;
        text-align: center;
        position: relative;
        display: none;
      }

      .widget-header h3 {
        margin: 0;
        font-size: 18px;
        font-weight: 600;
      }

      .widget-header .close-button {
        position: absolute;
        top: 15px;
        right: 15px;
        background: rgba(255, 255, 255, 0.2);
        border: none;
        color: white;
        width: 30px;
        height: 30px;
        border-radius: 50%;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: background 0.3s ease;
      }

      .widget-header .close-button:hover {
        background: rgba(255, 255, 255, 0.3);
      }

      /* Chat Messages Area */
      .chat-area-zena .chat-messages {
          flex: 1 1;
          padding: 20px;
          background: #F8F9FA;
          margin-bottom: 80px;
      }

      .message {
        margin-bottom: 15px;
        display: flex;
        align-items: flex-start;
      }

      .message.user {
        justify-content: flex-end;
      }

      .message.assistant {
        justify-content: flex-start;
      }

      .message-content {
        max-width: 80%;
        padding: 12px 16px;
        border-radius: 18px;
        line-height: 1.4;
        font-family: var(--font-primary)!important;
        font-weight: 400;
        font-size: 14px;
        word-wrap: break-word;
      }
      .message-content a,
      .message-content a:hover,
      .message-content a:focus
      {
      color: var(--theme-color)!important;
      text-decoration: underline!important;
      }


      .message.user .message-content {
        background: linear-gradient(135deg, var(--theme-color) 0%, var(--theme-color-dark) 100%);
        color: white;
        border-bottom-right-radius: 6px;
      }
      .message.user .message-content a,
      .message.user .message-content a:hover,
      .message.user .message-content a:focus
      {
        color: white!important;
      }

      .message.assistant .message-content {
        background: white;
        color: #333;
        border: 1px solid #e9ecef;
        border-bottom-left-radius: 6px;
      }

      /* Input Area */
      .widget-panel .chat-input-area {
        padding: 20px 2%;
          background: white;
          border-top: 1px solid #E9ECEF;
          position: absolute;
          bottom: 0px;
          left: 0;
          width: -webkit-fill-available;
      }

      .input-container {
        display: flex;
        align-items: center;
        gap: 5px;
      }

      /* Small Call Button - Next to send button */
      .call-button-small {
        background: var(--theme-color);
        color: white;
        border: none;
        width: 40px;
        height: 40px;
        border-radius: 50%;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: all 0.3s ease;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
      }

      .call-button-small:hover {
        background: var(--theme-color-light);
        transform: scale(1.1);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
      }

      .call-button-small:disabled {
        opacity: 0.6;
        cursor: not-allowed;
        transform: none;
      }

      /* Small End Call Button - Next to send button */
      .end-call-button-small {
        background: var(--theme-color);
        color: white;
        border: none;
        width: 35px;
        height: 35px;
        border-radius: 50%;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: all 0.3s ease;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
      }

      .end-call-button-small:hover {
        background: var(--theme-color-light);
        transform: scale(1.1);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
      }

      /* Icon styles for small call buttons */
      .icon-phone-small,
      .icon-phone-end-small {
        font-size: 16px;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .send-message-area {
        flex: 1 1;
        display: flex;
        align-items: center;
        gap: 5px;
        position: relative;
      }
      .send-message-area  .button-style1 {
      position: absolute;
          right: 12px;
          top: 8px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: transform 0.3s ease;
          background-color: transparent;
          color: black;
          border: 0;
          padding: 0;
          width: initial;
          min-height: initial;

      }

      .chat-input {
        padding: 8px 16px !important;
        border: 2px solid #e9ecef !important;
        border-radius: 25px !important;
        font-size: 13px !important;
        outline: none !important;
        transition: border-color 0.3s ease !important;
        min-height: initial !important;
        line-height: initial !important;
        margin: 0 !important;
        background-color: white!important;
        color: #838383!important;
        flex: 1;
      }

      .chat-input:focus {
        border-color: var(--theme-color);
      }

      .send-button {
        background: linear-gradient(135deg, var(--theme-color) 0%, var(--theme-color-dark) 100%);
        color: white;
        border: none;
        width: 30px;
        height: 30px;
        min-height: initial;
        border-radius: 50%;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: transform 0.3s ease;
      }

      .send-button:hover {
        transform: scale(1.1);
      }

      .send-button:disabled {
        opacity: 0.6;
        cursor: not-allowed;
      }



      /* Voice Button */
      .voice-button {
        background: linear-gradient(135deg, var(--theme-color) 0%, var(--theme-color-dark) 100%);
        color: white;
        border: none;
        width: 40px;
        height: 40px;
        border-radius: 50%;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: all 0.3s ease;
      }

      .voice-button:hover {
        transform: scale(1.1);
      }

      /* Suggestive Questions */
      .suggestive-questions {
        display: flex;
        flex-direction: column;
        gap: 10px;
        padding: 20px;
        margin-top: 10px;
      }

      .question-suggestion {
        background: #ffffff;
        border: 1px solid #e9ecef;
        border-radius: 20px;
        padding: 12px 20px;
        cursor: pointer;
        font-size: 14px;
        color: #495057;
        text-align: center;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
      }

      .question-suggestion:hover {
        background: #e9ecef;
        border-color: #dee2e6;
        transform: translateY(-1px);
        box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
      }

      .question-suggestion:active {
        transform: translateY(0);
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
      }

      .voice-button.recording {
        background: var(--color-error);
        animation: pulse 1.5s infinite;
      }

      /* Animations */
      @keyframes slideInUp {
        from {
          opacity: 0;
          transform: translateY(20px);
        }

        to {
          opacity: 1;
          transform: translateY(0);
        }
      }

      @keyframes pulse {
        0% {
          transform: scale(1);
        }

        50% {
          transform: scale(1.1);
        }

        100% {
          transform: scale(1);
        }
      }



      .top-bar {
        display: flex;
        align-items: center;
        justify-content: space-between;
        background: linear-gradient(to right, #111, #222);
        padding: 12px 16px;
        border-radius: 12px 12px 0 0;
        color: white;
      }

      .title {
        font-size: 16px;
        font-weight: 600;
        flex: 1;
        text-align: left;
        margin-left: 8px;
      }

      .icon-btn {
          background: none !important;
          border: none !important;
          padding: 4px !important;
          cursor: pointer !important;
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
          width: initial !important;
      }

      .icon-btn svg {
        width: 20px;
        height: 20px;
        stroke: white;
      }

      .lang-select-box {
        position: relative;
        display: inline-flex;
        align-items: center;
        height: 38px;
        border: 1px solid #e5e7eb;
        border-radius: 10px;
        padding: 0 28px 0 12px;
        background: #fff;
        min-width: 160px;
      }
      .lang-dropdown {
        position: absolute;
        margin-top: 44px;
        background: #fff;
        border: 1px solid #e5e7eb;
        border-radius: 10px;
        box-shadow: 0 6px 24px rgba(0, 0, 0, 0.08);
        width: 296px;
        max-height: 300px;
        overflow: hidden;
        z-index: 50;
      }



      .live-call-area {
        display: flex;
        justify-content: space-around;
        position: absolute;
        left: 0;
        top: 51px;
        z-index: 100;
        width: 100%;
        background-color: white;
        border-bottom: 1px solid #e9ecef;
      }

      /* Responsive Design */
      @media (max-width: 1536px) {
      .widget-panel {
        height: 440px;
      }
      .widget-panel-middle-scroll-area.chat-area-zena {
          height: 263px;
      }
      }

      @media (max-width: 480px) {
        .widget-panel-middle-scroll-area.chat-area-zena {
          height: 41vh;
        }
    
        .lang-dropdown {
          width: 260px;
          max-height: 225px;
        }
        .chat-widget-call-btn {
          bottom: 100px;
        }
        .widget-panel {
              width: 90vw !important;
              height: 60vh !important;
              right: 1vw !important;
              bottom: 80px !important;
        }
        .chat-widget-container.bubble-open {
          bottom: 15px;
        }

        .message-content {
          max-width: 90%;
        }
      }

      /* Loading States */
      .loading {
        display: flex;
        align-items: center;
        justify-content: center;
        padding-bottom: 10px;
        color: #666;
      }

      .loading-dots {
        display: flex;
        gap: 4px;
      }

      .loading-dots span {
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background: var(--theme-color);
        animation: loadingDots 1.4s infinite ease-in-out;
      }

      .loading-dots span:nth-child(1) {
        animation-delay: -0.32s;
      }

      .loading-dots span:nth-child(2) {
        animation-delay: -0.16s;
      }

      @keyframes loadingDots {

        0%,
        80%,
        100% {
          transform: scale(0);
        }

        40% {
          transform: scale(1);
        }
      }

      /* Welcome Message - Matching the screenshot */
      .welcome-message {
        text-align: center;
        color: #666;
        font-size: 16px;
        padding: 40px 20px;
        background: transparent;
        border: none;
        margin: 0;
      }

      .welcome-message p {
        margin: 0;
        font-weight: 400;
      }

      /* Positioning to mimic your floating button */
      .call-cta {
        position: fixed;
        right: 20px;
        bottom: 20px;
        z-index: 1000;

        transition: width .28s ease, padding .28s ease, border-radius .28s ease;
        display: inline-flex;
        align-items: center;
        overflow: hidden;
      }

      /* Default pill width feels good for label + icon */
      .call-cta:not(.contract) {
        width: 180px;
        justify-content: flex-start;
      }

      /* When open/connected, collapse to a circle */
      .call-cta.contract {
        width: 56px;
        padding-inline: 0 !important;
        justify-content: center;
        border-radius: 999px;
      }

      /* Label transition (fade & slide up) */
      .call-cta .label {
        margin-left: 8px;
        transition: opacity .22s ease, transform .22s ease;
        white-space: nowrap;
      }

      .call-cta .label.hide {
        opacity: 0;
        transform: translateY(-6px);
        pointer-events: none;
      }

      /* Optional: subtle nudge for the icon when collapsed */
      .call-cta.contract .anticon {
        transition: transform .22s ease;
        transform: translateY(1px);
      }

      .icon-phone {
        margin-bottom: 5px;
        height: 12px;
      }

      .mr-10 {
        margin-right: 10px;
      }

      .icon-arrow {
        display: none;
      }

      .chat-widget-container.bubble-open .start-call,
      .chat-widget-container.bubble-open .icon-circle,
      .chat-widget-container.bubble-open .icon-phone {
        display: none;
      }

      .chat-widget-container.bubble-open .icon-arrow {
        display: block;
      }

      .chat-widget-container.bubble-open .icon-arrow svg {
        rotate: 180deg;
        font-size: 15px;
      }

      .chat-widget-container.bubble-open .call-button {
        padding: 12px 11px 8px 11px;
      }

      .chat-messages {
        position: relative;
      }

      .agent-icon-display {
          display: flex;
          flex-direction: column;
          gap: 10px;
          padding: 5px 20px;
          margin-top: 10px;
      }

      .agent-icon-display img {
        width: 100%;
        border-radius: 100px;
      }

      /* Call Button - Only show when not in call mode */
      .ai-button-start {
        background: var(--theme-color);
        color: white;
        padding: 13px 5px 10px 7px;
        font-size: 14px;
        font-weight: 500;
        white-space: nowrap;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
        width: 60px;
        height: 60px;
        border-radius: 100px;
        border: 5px solid white;
        position: relative;
        top: -25px;
        text-align: center;
        cursor: pointer;
        transition: all 0.3s ease;
      }

      .ai-button-start:hover {
        cursor: pointer;
        background-color: var(--theme-color-light);
        transform: scale(1.05);
      }

      .ai-button-start:focus {
        background-color: var(--theme-color);
        outline: none;
      }

      .ai-button-start:visited {
        background-color: var(--theme-color);
      }

      .ai-button-start:disabled {
        opacity: 0.6;
        cursor: not-allowed;
        transform: none;
      }

      /* End Call Button - Only show when in call mode */
      .ai-button-end-call {
        background: var(--theme-color);
        color: white;
        padding: 13px 5px 10px 7px;
        font-size: 14px;
        font-weight: 500;
        white-space: nowrap;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
        width: 60px;
        height: 60px;
        border-radius: 100px;
        border: 5px solid white;
        position: relative;
        top: -25px;
        text-align: center;
        cursor: pointer;
        transition: all 0.3s ease;
      }

      .ai-button-end-call:hover {
        background-color: var(--theme-color-light);
        transform: scale(1.05);
      }

      .ai-button-end-call:focus {
        background-color: var(--color-error);
        outline: none;
      }

      /* Call Status Indicator */
      .call-status {
        position: absolute;
        top: -40px;
        left: 0;
        right: 0;
        text-align: center;
      }

      .call-indicator {
        background: var(--theme-color);
        color: white;
        padding: 8px 16px;
        border-radius: 20px;
        font-size: 12px;
        font-weight: 600;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
        animation: pulse-call 2s infinite;
      }

      /* Call Status Indicator at Top of Chat Messages */
      .call-status-top {
        text-align: center;
        padding: 10px;
      }

      .call-indicator-top {
        background: var(--theme-color);
        color: white;
        padding: 8px 16px;
        border-radius: 20px;
        font-size: 12px;
        font-weight: 600;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
        animation: pulse-call 2s infinite;
        display: inline-block;
      }

      @keyframes pulse-call {
        0% {
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
        }

        50% {
          box-shadow: 0 2px 16px rgba(0, 0, 0, 0.6);
        }

        100% {
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
        }
      }

      /* Icon styles for call buttons */
      .icon-phone-2 {
        font-size: 20px;
      }

      .icon-phone-end {
        font-size: 20px;
      }

      /* Disabled state for input when connecting */
      .chat-input:disabled {
        background-color: #f8f9fa;
        cursor: not-allowed;
        opacity: 0.7;
      }

      .send-button:disabled {
        opacity: 0.6;
        cursor: not-allowed;
        transform: none;
      }

      /* Call mode specific styles */
      .chat-widget-container.call-mode .chat-input {
        border-color: var(--theme-color);
      }

      .chat-widget-container.call-mode .send-button {
        background: linear-gradient(135deg, var(--theme-color) 0%, var(--theme-color-light) 100%);
      }

      /* New call button with theme color */
      .send-button.style2 {
        background: var(--theme-color) !important;
        color: white !important;
        border: 1px solid var(--theme-color) !important;
        transition: all 0.3s ease;
      }

      .send-button.style2:hover:not(:disabled) {
        background: var(--theme-color-light) !important;
        border-color: var(--theme-color-light) !important;
        transform: translateY(-1px);
        box-shadow: 0 4px 12px var(--theme-color-transparent);
      }

      /* Responsive adjustments for call button */
      @media (max-width: 480px) {

        .ai-button-start,
        .ai-button-end-call {
          width: 50px;
          height: 50px;
          top: -20px;
        }

        .call-status {
          top: -35px;
        }

        .call-indicator {
          font-size: 11px;
          padding: 6px 12px;
        }
      }

      /* Authentication Form Styles */
      .auth-form-container {
        padding: 20px;
        background: white;
        border-radius: 12px;
        display: flex;
        flex-direction: column;
        justify-content: center;
        margin: 16px;
          border: .88px solid #e8e8e8;
        min-height: 100%;
      }

      .auth-form-header {
        text-align: center;
        margin-bottom: 24px;
      }

      .auth-form-header h3 {
        margin: 0 0 8px 0;
        font-size: 18px;
        font-weight: 600;
        color: var(--color-text-primary);
      }

      .auth-form-header p {
        margin: 0;
        font-size: 14px;
        color: var(--color-text-secondary);
      }

      .auth-form {
        display: flex;
        flex-direction: column;
        gap: 20px;
      }

      .input-group {
        display: flex;
        flex-direction: column;
        gap: 6px;
      }

      .input-group label {
        font-size: 14px;
        font-weight: 500;
        color: var(--color-text-primary);
        margin-left: 2px;
      }

      .auth-input {
        padding: 12px 16px;
        border: 2px solid #e1e5e9;
        border-radius: 8px;
        font-size: 14px;
        font-family: inherit;
        transition: all 0.2s ease;
        background: white;
      }

      .auth-input:focus {
        outline: none;
        border-color: var(--theme-color);
        box-shadow: 0 0 0 3px var(--theme-color-transparent);
      }

      .auth-input::placeholder {
        color: #9ca3af;
      }

      .auth-input:disabled {
        background-color: #f8f9fa;
        cursor: not-allowed;
        opacity: 0.7;
      }

      .error-message {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 12px 16px;
        background: #fef2f2;
        border: 1px solid #fecaca;
        border-radius: 8px;
        color: #dc2626;
        font-size: 14px;
        font-weight: 500;
      }

      .error-icon {
        font-size: 16px;
      }

      .auth-buttons {
        display: flex;
        flex-direction: column;
        gap: 12px;
        margin-top: 8px;
      }

      .submit-button {
        background: linear-gradient(135deg, var(--theme-color) 0%, var(--theme-color-light) 100%);
        color: white;
        border: none;
        padding: 14px 24px;
        border-radius: 8px;
        font-size: 14px;
        font-weight: 600;
        cursor: pointer;
        transition: all 0.2s ease;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        min-height: 48px;
      }

      .submit-button:hover:not(:disabled) {
        transform: translateY(-1px);
        box-shadow: 0 4px 12px var(--theme-color-transparent);
      }

      .submit-button:active:not(:disabled) {
        transform: translateY(0);
      }

      .submit-button:disabled {
        opacity: 0.6;
        cursor: not-allowed;
        transform: none;
      }

      .skip-button {
        background: transparent;
        color: var(--color-text-secondary);
        border: 2px solid #e1e5e9;
        padding: 12px 24px;
        border-radius: 8px;
        font-size: 14px;
        font-weight: 500;
        cursor: pointer;
        transition: all 0.2s ease;
        min-height: 44px;
      }

      .skip-button:hover {
        background: #f8f9fa;
        border-color: #cbd5e1;
        color: var(--color-text-primary);
      }

      .skip-button:active {
        transform: translateY(0);
      }

      .loading-spinner {
        width: 16px;
        height: 16px;
        border: 2px solid rgba(255, 255, 255, 0.3);
        border-top: 2px solid white;
        border-radius: 50%;
        animation: spin 1s linear infinite;
      }

      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }

      /* Responsive adjustments for auth form */
      @media (max-width: 480px) {
        .auth-form-container {
          padding: 16px;
        }

        .auth-form-header h3 {
          font-size: 16px;
        }

        .auth-form-header p {
          font-size: 13px;
        }

        .auth-input {
          padding: 10px 14px;
          font-size: 16px;
        }

        .submit-button,
        .skip-button {
          padding: 12px 20px;
          font-size: 16px;
        }
      }



      /* .settings-card-main {
        padding: 20px 16px 20px;
      } */

      .faq-card-main {
        padding: 16px 16px 16px;
        border-radius: 8px;
        margin: 0px;
        position: relative;
        z-index: 1;
      }

      .faq-card-main:hover {

      }
      .faq-card {
          background: #fff;
          border-radius: 12px;
          overflow: hidden;
          width: 96%;
          max-width: 100%;
          margin: 0px auto 20px;
          border: .88px solid #e8e8e8;
          padding: 2%;
          transition: all 0.2s ease;
      }

      .faq-card:hover {
          border-color: #d0d0d0;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
      }

      .faq-row {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 8px 8px;
        border-radius: 8px;
        cursor: pointer;
        transition: all 0.2s ease;
        border: 1px solid transparent;
      }
      .faq-row .faq-question{
        font-size: 14px;
        color: #333;
        font-weight: 500;
        line-height: 1.4;
        flex: 1;
        margin-right: 12px;
      }

      .faq-row .arrow-right{
        position: relative;
        top: -1px;
        right: 2px;
        color: #999;
        transition: all 0.2s ease;
      }

      .faq-row:hover .arrow-right {
        color: #666;
        transform: translateX(2px);
      }

      .faq-row:last-child {
        border-bottom: none;
      }

      .faq-row:hover {
        background: rgba(67, 67, 67, 0.08);
        transform: translateX(2px);
        border-color: rgba(67, 67, 67, 0.2);
      }

      .faq-row:active {
        transform: translateX(1px);
        background: rgba(67, 67, 67, 0.12);
        border-color: rgba(67, 67, 67, 0.3);
      }

      /* FAQ Loading and No FAQs States */
      .loading-spinner-small {
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 20px;
        color: #666;
        font-size: 14px;
        background: rgba(0, 0, 0, 0.02);
        border-radius: 6px;
        margin: 8px 0;
      }

      .no-faqs-message {
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 20px;
        color: #999;
        font-size: 14px;
        font-style: italic;
        text-align: center;
        background: rgba(0, 0, 0, 0.02);
        border-radius: 6px;
        margin: 8px 0;
      }

      /* FAQ Header and Refresh Button */
      .faq-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 12px 8px;
        border-bottom: 1px solid #f0f0f0;
        margin-bottom: 8px;
        background: rgba(0, 0, 0, 0.02);
        border-radius: 6px 6px 0 0;
      }

      .faq-header span {
        font-weight: 600;
        color: #333;
        font-size: 14px;
      }

      .refresh-faqs-btn {
        background: none;
        border: none;
        cursor: pointer;
        padding: 6px;
        border-radius: 6px;
        font-size: 16px;
        transition: all 0.2s ease;
        opacity: 0.7;
        display: flex;
        align-items: center;
        justify-content: center;
        min-width: 28px;
        min-height: 28px;
      }

      .refresh-faqs-btn:hover {
        opacity: 1;
        background: rgba(0, 0, 0, 0.05);
        transform: scale(1.1);
      }

      .refresh-faqs-btn:disabled {
        opacity: 0.4;
        cursor: not-allowed;
        transform: none;
      }

      .retry-btn {
        background: #007bff;
        color: white;
        border: none;
        border-radius: 4px;
        cursor: pointer;
        transition: background-color 0.2s ease;
      }

      .retry-btn:hover {
        background: #0056b3;
      }

      /* Error message styling */
      .error-message {
        display: flex;
        align-items: center;
        padding: 12px;
        background: #fef2f2;
        border: 1px solid #fecaca;
        border-radius: 6px;
        color: #dc2626;
        font-size: 13px;
        margin: 8px 0;
      }

      .error-icon {
        margin-right: 8px;
        font-size: 14px;
      }

      /* Test connection button */
      .test-connection-btn {
        transition: all 0.2s ease;
      }

      .test-connection-btn:hover {
        background: #0056b3 !important;
        transform: translateY(-1px);
      }

      .test-connection-btn:active {
        transform: translateY(0);
      }

      .chevron {
        color: #999;
        font-size: 16px;
      }



      /* Widget Menu - Tabs Navigation */
      .widget-menu {
        background: white;
        box-shadow: 0px -1px 3px 0 rgba(0, 0, 0, .1019607843);
        border-radius: 0 0 12px 12px;
        position: absolute;
        bottom: 0;
        left: 0;
        width: 100%;
      }

      /* container: give padding and spacing between buttons */
      .widget-menu .tabs-navigation {
        display: flex;
        background: white;
        padding: 0px;
        border-radius: 0 0 12px 12px;
        gap: 10px;
      }

      .widget-menu .tabs-navigation .tab-item {
        flex: 1;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 7px;
        background: none;
        border: none;
        padding: 12px 4px 12px 4px;
        cursor: pointer;
        transition: all 0.2s ease;
        color: #8c8c8c;
        position: relative;
        min-height: 48px;
        justify-content: center;
        border-radius: 10px;
      }

      .widget-menu .tabs-navigation  .tab-item:hover { color: var(--theme-color-light); background: none; }

      /* Active state: darker text + a light bg that spans the full button width */
      .widget-menu .tabs-navigation .tab-item.active {
        color: var(--theme-color);
      }

      /* Active indicator ON TOP and full width of the active button */
      .widget-menu .tabs-navigation .tab-item.active::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 2px;
        background: var(--theme-color);
        border-radius: 2px;
      }

      /* keep your icon/text styles */
      .widget-menu .tabs-navigation .tab-item svg {
        width: 20px;
        height: 20px;
        stroke: currentColor;
        stroke-width: 2;
        fill: none;
      }
      .widget-menu .tabs-navigation .tab-item span {
        font-size: 13px;
        line-height: 1.2;
        font-weight: 400;
        text-align: center;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
      }

      /* Responsive */
      @media (max-width: 480px) {
        .tabs-navigation { padding: 0px; gap: 8px; }
        .widget-menu .tabs-navigation .tab-item { padding: 8px 2px; min-height: 44px; }
        .widget-menu .tabs-navigation .tab-item svg { width: 18px; height: 18px; }
        .widget-menu .tabs-navigation .tab-item span { font-size: 11px; }
      }



      /* Card */
      .settings-card-main {
        background: #ffffff;
        border: 1px solid #e5e7eb;
        border-radius: 16px;
        padding: 18px 20px;
        box-shadow: 0 1px 0 rgba(0,0,0,0.02);
          margin: 16px;
      }

      /* Row: text left, control right */
      .settings-row {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 16px;
        flex-direction: column;
      }

      /* Title + subtitle */
      .settings-title {
        margin: 0 0 4px 0!important;
        font-size: 16px!important;
        font-weight: 700!important;
        color: #111827!important;
      }

      .settings-subtitle {
        margin: 0!important;
        font-size: 14px!important;
        color: #6b7280!important;
      }

      /* Stack on small screens */
      @media (max-width: 640px) {
        .settings-row {
          flex-direction: column;
          align-items: flex-start;
        }
      }
    `;
  };

  return (
    <div ref={shadowHostRef} className="chat-widget-shadow-host" style={{ display: 'block !important' }}>
      {shadowRootRef.current && ReactDOM.createPortal(children, shadowRootRef.current.getElementById('chat-widget-root'))}
    </div>
  );
};

const AGENT_ID = 'agent_3201k3bb0xnmf8db49yd6cya7nsc';
const DOMAIN = 'yensa.myshopify.com/'
const ACCESS_TOKEN = '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'

// const AGENT_ID = 'agent_1601k26tasqtejp9hvm2zpm9gz06';
// const DOMAIN = 'dl1961trial.myshopify.com'
// const ACCESS_TOKEN = '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'

// const AGENT_ID = '';
// const DOMAIN = ''
// const ACCESS_TOKEN=''

const THEME_COLOR = "#b5284b"

function InnerComponent({
  agentId,
  domain,
  themeColor,
  accessToken,
  questions,
  isVoice
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [isMicGranted, setIsMicGranted] = useState(false);
  const [inputMessage, setInputMessage] = useState('');
  const [isConnecting, setIsConnecting] = useState(false);
  const [isCallActive, setIsCallActive] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [localIsSpeaking, setLocalIsSpeaking] = useState(false);

  // Validate ElevenLabs configuration
  const validateConfiguration = () => {
    const issues = [];

    // Check if ElevenLabs package is available
    try {
      if (typeof useConversation === 'undefined') {
        issues.push('ElevenLabs package not found');
      }
    } catch (e) {
      issues.push('ElevenLabs package not available');
    }

    if (!agentId) {
      issues.push('Agent ID is missing');
    } else if (!agentId.startsWith('agent_')) {
      issues.push('Agent ID format is invalid (should start with "agent_")');
    }

    if (!domain) {
      issues.push('Domain is missing');
    }

    if (!accessToken) {
      issues.push('Access token is missing');
    } else if (!accessToken.startsWith('shpat_')) {
      issues.push('Access token format is invalid (should start with "shpat_")');
    }

    // Check if we're on HTTPS (required for microphone access)
    if (window.location.protocol !== 'https:' && window.location.hostname !== 'localhost') {
      issues.push('HTTPS is required for microphone access');
    }

    // Check if getUserMedia is supported
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      issues.push('Microphone access is not supported in this browser');
    }

    return issues;
  };

  const audioStream = useRef(null)
  const chatEndRef = useRef(null);
  const connectionAbortRef = useRef(false);

  // Message handler
  const handleIncomingMessage = (msg) => {
    console.log('📨 MESSAGE RECEIVED:', msg);
    if (!msg) return;

    const messageText = msg?.text || msg?.message || msg?.content;

    const isUserLike =
      msg.type === 'transcript' ||
      msg.type === 'user_transcript' ||
      msg.type === 'user_message' ||
      msg.source === 'user' ||
      msg.source === 'microphone' ||
      msg.from === 'user' ||
      msg.role === 'user' ||
      msg.speaker === 'user' ||
      (msg.type === 'transcript' && !msg.source);

    const isAiLike =
      msg.source === 'ai' ||
      msg.type === 'agent_response' ||
      msg.type === 'agent_message' ||
      msg.from === 'ai' ||
      msg.role === 'assistant';

    if (messageText) {
      if (isUserLike) {
        setMessages((prev) => [
          ...prev,
          { type: msg.type || 'transcript', text: messageText, from: 'user' },
        ]);
      } else if (isAiLike) {
        setMessages((prev) => [
          ...prev,
          { type: 'agent_response', text: messageText, from: 'ai' },
        ]);
      } else {
        setMessages((prev) => [
          ...prev,
          { type: msg.type || 'voice', text: messageText, from: 'user' },
        ]);
      }
    }
  };

  const addErrorMessageToChat = (errorMessage, guidanceMessage) => {
    setMessages((prev) => [
      ...prev,
      {
        type: 'system_error',
        text: errorMessage,
        from: 'system',
        guidance: guidanceMessage
      },
    ]);
  };

  const { startSession, endSession, status, isSpeaking, sendUserMessage } =
    useConversation({
      agentId,
      textOnly: isVoice ? false : true,
      overrides: {
        conversation: {
          textOnly: isVoice ? false : true
        }
      },
      onConnect: () => {
        console.log('✅ Connected to ElevenLabs')
      },
      onDisconnect: () => {
        console.log('❌ Disconnected from ElevenLabs')
        setIsCallActive(false);
        setLocalIsSpeaking(false);
      },
      onMessage: handleIncomingMessage,
      onError: (err) => {
        console.error('❌ ElevenLabs Error:', err);
        // Handle specific ElevenLabs errors
        if (err?.message?.includes('agent')) {
          console.error('Agent configuration error - check agent ID and access token');
        } else if (err?.message?.includes('network')) {
          console.error('Network error - check internet connection');
        } else if (err?.message?.includes('authentication')) {
          console.error('Authentication error - check access token');
        }
      },
      onDebug: (debug) => {
        console.debug('🔍 ElevenLabs Debug:', debug);
      },
    });

  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, localIsSpeaking]);

  useEffect(() => {
    console.log('🚀 Component mounted, initializing conversation hook...');
    setIsInitialized(false);
  }, []);

  useEffect(() => {
    setLocalIsSpeaking(isSpeaking);
    console.log('🎤 Speaking state changed:', isSpeaking);
  }, [isSpeaking]);

  useEffect(() => {
    if (status === 'disconnected' && localIsSpeaking) {
      setLocalIsSpeaking(false);
      console.log('🔇 Speaking state reset due to disconnection');
    }
  }, [status, localIsSpeaking]);

  const requestMic = async () => {
    try {
      console.log('🎤 Requesting microphone access...');

      // Check if getUserMedia is supported
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('getUserMedia not supported in this browser');
      }

      // Check if we're on HTTPS or localhost
      if (window.location.protocol !== 'https:' && window.location.hostname !== 'localhost') {
        throw new Error('HTTPS_REQUIRED');
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 44100,
          channelCount: 1,
        },
      });

      console.log('✅ Microphone access granted');
      console.log('🎤 Audio tracks:', stream.getAudioTracks());
      setIsMicGranted(true);
      audioStream.current = stream;
      return stream;
    } catch (error) {
      console.error('❌ Microphone access denied:', error);
      console.error('❌ Microphone error details:', {
        name: error.name,
        message: error.message,
        code: error.code
      });

      setIsMicGranted(false);

      // Provide more specific error messages
      if (error.name === 'NotAllowedError') {
        throw new Error('Microphone access denied by user. Please allow microphone access in your browser settings.');
      } else if (error.name === 'NotFoundError') {
        throw new Error('No microphone found. Please connect a microphone and try again.');
      } else if (error.name === 'NotSupportedError') {
        throw new Error('Your browser does not support microphone access. Please try a different browser.');
      } else if (error.name === 'SecurityError') {
        throw new Error('Microphone access blocked due to security restrictions. Please check your browser settings.');
      } else if (error.message === 'HTTPS_REQUIRED') {
        throw new Error('Microphone access requires HTTPS. Please use a secure connection.');
      } else if (error.message === 'getUserMedia not supported in this browser') {
        throw new Error('Your browser does not support microphone access. Please try a different browser.');
      } else {
        throw error;
      }
    }
  };

  const startCall = async (withButton) => {
    if (connectionAbortRef.current) return;
    try {
      if (withButton) {
        setMessages([])
        setIsInitialized(false)
        // Don't set isConnecting to false here to allow proper connecting state display
      }

      console.log('📞 Starting call...');
      console.log('🔧 Call configuration:', { agentId, domain, accessToken: accessToken ? '***' : 'none' });

      // Validate configuration first
      const configIssues = validateConfiguration();
      if (configIssues.length > 0) {
        const issuesText = configIssues.join(', ');
        throw new Error(`Configuration issues: ${issuesText}`);
      }

      await endSession();
      setIsConnecting(true);

      setIsCallActive(true);

      // Request microphone access first
      console.log('🎤 Requesting microphone access...');
      isVoice && await requestMic();

      const sessionOptions = {
        agentId,
        connectionType: isVoice ? 'webrtc' : 'websocket',
      };

      if (accessToken) {
        sessionOptions.dynamicVariables = { access_token: accessToken, store_url: domain };
      }

      console.log('🚀 Starting ElevenLabs session with options:', sessionOptions);
      await startSession(sessionOptions);

      console.log('✅ Call started successfully');
      if (withButton) setIsInitialized(true)
      if (!isInitialized) {
        setIsInitialized(true);
        console.log('✅ System marked as initialized after successful call');
      }
    } catch (error) {
      console.error('❌ Error starting call:', error);
      console.error('❌ Error details:', {
        name: error.name,
        message: error.message,
        stack: error.stack,
        code: error.code
      });

      setIsCallActive(false);

      // Handle specific error types with chat guidance
      if (error?.name === 'NotAllowedError') {
        addErrorMessageToChat(
          'Microphone access denied',
          `To enable ${isVoice ? "voice calls" : "chat"}, please click the microphone icon in your browser\'s address bar and select "Allow" when prompted. Then try refresh to starting the ${isVoice ? "call" : "chat"} again.`
        );
      } else if (error?.name === 'NotFoundError') {
        addErrorMessageToChat(
          'No microphone found',
          'Please connect a microphone to your device and refresh the page, or try using a different browser/device that has microphone support.'
        );
      } else if (error?.name === 'NotSupportedError') {
        addErrorMessageToChat(
          'Browser not supported',
          `Your browser doesn\'t support ${isVoice ? "voice calls" : "chat"}. Please try using a modern browser like Chrome, Firefox, Safari, or Edge for the best experience.`
        );
      } else if (error?.name === 'SecurityError') {
        addErrorMessageToChat(
          'Security restrictions',
          'Microphone access is blocked due to security settings. Please check your browser privacy settings or try accessing this page over HTTPS.'
        );
      } else if (error?.message === 'HTTPS_REQUIRED') {
        addErrorMessageToChat(
          'HTTPS required',
          `${isVoice ? "Voice calls" : "chat"} require a secure connection. Please make sure you\'re accessing this page over HTTPS (look for the lock icon in the address bar).`
        );
      } else if (error?.message === 'AGENT_ID_MISSING') {
        addErrorMessageToChat(
          'Configuration error',
          'The AI assistant is not properly configured. Please contact support or try refreshing the page.'
        );
      } else if (error?.message?.includes('agent')) {
        addErrorMessageToChat(
          'Agent configuration error',
          'There\'s an issue with the AI assistant setup. Please contact support or try refreshing the page.'
        );
      } else if (error?.message?.includes('network') || error?.message?.includes('fetch')) {
        addErrorMessageToChat(
          'Connection error',
          'Unable to connect to the AI assistant. Please check your internet connection and try again in a moment.'
        );
      } else {
        // Show the actual error message if available
        const errorMsg = error?.message || 'Unknown error occurred';
        addErrorMessageToChat(
          'Unable to start call',
          `An error occurred while starting the ${isVoice ? "call" : "chat"}: ${errorMsg}. Please try again or contact support if the problem persists.`
        );
      }
    } finally {
      // Add a small delay to ensure connecting state is visible even with immediate errors
      setTimeout(() => {
        setIsConnecting(false);
      }, 500);
    }
  };

  const endCall = async (withButton) => {
    try {
      await endSession();
      if (!withButton) {
        setIsCallActive(false);
        setIsInitialized(false)
        setMessages([])
        setIsConnecting(false)
        setLocalIsSpeaking(false);
        setInputMessage("")
      }
      setIsCallActive(false);
      audioStream?.current?.getTracks().forEach(track => track.stop());
      console.log('🔇 Speaking state reset after call end');
      console.log('✅ Call ended');
    } catch (error) {
      console.error('❌ Error ending call:', error);
    }
  };

  const handleSendText = async () => {
    const text = inputMessage.trim();
    if (!text || status !== 'connected') return;

    setMessages((prev) => [
      ...prev,
      { type: 'user_message', text, from: 'user' },
    ]);
    setInputMessage('');

    try {
      await sendUserMessage(text);
    } catch (error) {
      console.error('❌ Error sending message:', error);
      setMessages((prev) => [
        ...prev,
        {
          type: 'system',
          text: 'Failed to send. Please try again.',
          from: 'system',
        },
      ]);
    }
  };

  const handleSendMessage = async (text) => {
    if (!text || status !== 'connected') return;

    setMessages((prev) => [
      ...prev,
      { type: 'user_message', text, from: 'user' },
    ]);

    try {
      await sendUserMessage(text);
    } catch (error) {
      console.error('❌ Error sending message:', error);
      setMessages((prev) => [
        ...prev,
        {
          type: 'system',
          text: 'Failed to send. Please try again.',
          from: 'system',
        },
      ]);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendText();
    }
  };

  function onOpenCall() {
    if (!(!isCallActive && isInitialized)) {
      setTimeout(() => {
        if (status !== 'connected' && !isConnecting) {
          startCall();
        }
      }, 500);
    }
    if (chatEndRef.current) {
      chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }

  const toggleWidget = () => {
    if (isOpen && !isInitialized) return
    setIsOpen((prev) => {
      const next = !prev;
      if (next) {
        onOpenCall()
      } else {
        endCall()
      }
      return next;
    });
  };

  const componentDecorator = (href, text, key) => (
    <a href={href} key={key} target="_blank" rel="noopener noreferrer">
      {text}
    </a>
  );

  const renderMessage = (msg, index) => {
    const isUser = msg.from === 'user';
    const isSystemError = msg.type === 'system_error';

    if (isSystemError) {
      return (
        <div key={index} className="message system">
          <div className="message-content" style={{
            background: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: '12px',
            padding: '12px 16px',
            margin: '8px 0',
            maxWidth:'100%'
          }}>
            <div style={{
              color: '#dc2626',
              fontWeight: '600',
              fontSize: '14px',
              marginBottom: '4px'
            }}>
              ⚠️ {msg.text}
            </div>
            <div style={{
              color: '#7f1d1d',
              fontSize: '13px',
              lineHeight: '1.4'
            }}>
              <Linkify componentDecorator={componentDecorator}>
                {msg.guidance}
              </Linkify>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div key={index} className={`message ${isUser ? 'user' : 'assistant'}`}>
        <div className="message-content">
          <Linkify componentDecorator={componentDecorator}>
            {msg.text}
          </Linkify>
        </div>
      </div>
    );
  };

  function renderChat() {
    return (
      <>
        <div className="widget-header" style={{ background: themeColor }}>
          <h3>{""}</h3>
          <button
            className="close-button"
            onClick={toggleWidget}
            aria-label="Close chat widget"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path
                d="M18 6L6 18M6 6L18 18"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>

        {isVoice && <div className='live-call-area'>
          {isCallActive && (
            <div className="call-status-top">
              <span className="call-indicator-top" style={{ fontSize: 11 }}>🔴 Live Call - Voice & Text Active</span>
            </div>
          )}
          {localIsSpeaking && (
            <div className="loading" style={{ marginTop: 10 }}>
              <span style={{ marginRight: '10px', fontSize: 12 }}>
                {isCallActive ? 'AI is speaking' : 'AI is typing'}
              </span>
              <div className="loading-dots">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          )}
        </div>}

        <div className="chat-messages">
          {!isInitialized && messages?.length === 0 && (
            <div className="system-status">
              <span className="status-indicator">Connecting...</span>
              <div className="status-details">
                <small>Please wait while we prepare the AI assistant...</small>
              </div>
            </div>
          )}
          {messages.map(renderMessage)}
          {(messages.length === 0 || messages.length === 1) && !(!isCallActive && isInitialized) && questions?.length > 0 && isInitialized && (
            <>
              <div className='agent-icon-display'>{/* icon here if needed */}</div>
              <div className="suggestive-questions">
                {
                  questions?.map(it => {
                    return (
                      <div key={it?._id} className="question-suggestion" onClick={() => handleSendMessage(it?.question)}>
                        {it?.question}
                      </div>
                    )
                  })
                }
              </div>
            </>
          )}
          {!isCallActive && isInitialized && (
            <div style={{ display: 'flex', justifyContent: 'center', paddingBottom: '10px' }}>
              <span style={{ fontSize: 12 }}>{isVoice ? "Call" : "Conversation"} Ended!</span>
            </div>
          )}
          <div ref={chatEndRef} />
        </div>

        <div className="chat-input-area">
          <div className="input-container">
            {isCallActive && (
              <>
                <button
                  className="end-call-button-small"
                  onClick={() => endCall(true)}
                  disabled={!isInitialized}
                  aria-label="End voice call"
                  title="End voice call"
                >
                  <div className='icon-phone-end-small'>
                    {/* <svg height="1em" width="1em" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M3.7489 2.25C2.93286 2.25 2.21942 2.92142 2.27338 3.7963C2.6686 10.2041 7.79483 15.3303 14.2026 15.7255C15.0775 15.7795 15.7489 15.066 15.7489 14.25V11.958C15.7489 11.2956 15.3144 10.7116 14.6799 10.5213L12.6435 9.91035C12.1149 9.75179 11.542 9.89623 11.1518 10.2864L10.5901 10.8482C9.15291 10.0389 7.95998 8.84599 7.15074 7.40881L7.71246 6.84709C8.10266 6.45689 8.24711 5.88396 8.08854 5.35541L7.47761 3.31898C7.28727 2.6845 6.70329 2.25 6.04087 2.25H3.7489Z" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M3 15L15 3" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
                    </svg> */}
                    <p style={{ fontSize: 12 }}>End</p>
                  </div>
                </button>
                <div className='send-message-area'>
                  <input
                    type="text"
                    className="chat-input"
                    placeholder={isCallActive ? (isVoice ? "Type while on call..." : "Type your message...") : "Type your message..."}
                    value={inputMessage}
                    onChange={(e) => setInputMessage(e.target.value)}
                    onKeyDown={handleKeyDown}
                    disabled={isConnecting}
                  />
                  <button
                    onClick={handleSendText}
                    disabled={!inputMessage.trim() || isConnecting}
                    className="button-style1"
                  >
                    <slot name="icon-send" aria-hidden="true" class="flex text-lg">
                      <svg xmlns="http://www.w3.org/2000/svg" height="1.5em" width="1.5em" viewBox="0 0 20 20" fill="currentColor"><path d="M2.59413 5.1485C2.04 3.39377 3.86657 1.83482 5.51245 2.65776L16.47 8.13653C18.0055 8.90429 18.0055 11.0955 16.47 11.8633L5.51245 17.3421C3.86656 18.165 2.04 16.6061 2.59413 14.8513L3.86297 10.8333H7.50006C7.9603 10.8333 8.33339 10 8.33339 9.53976 8.33339 9.16667 7.9603 9.16667 7.50006 9.16667H3.86302L2.59413 5.1485Z" /></svg>
                    </slot>
                  </button>
                </div>
              </>
            )}

            {!isCallActive && isInitialized && (
              <button
                className="send-button style2"
                onClick={() => startCall(true)}
                disabled={isConnecting}
                aria-label="Start voice call"
                title="Start voice call"
                style={{
                  width: '100%',
                  borderRadius: 30,
                  height: 34,
                  background: themeColor,
                  border: `1px solid ${themeColor}`
                }}
              >
                New {isVoice ? "call" : "chat"}
                <div className='icon-phone-small' style={{ marginLeft: 5 }}>
                  {isVoice ? (
                    <svg height="1em" width="1em" viewBox="0 0 18 18" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                      <path d="M3.7489 2.25C2.93286 2.25 2.21942 2.92142 2.27338 3.7963C2.6686 10.2041 7.79483 15.3303 14.2026 15.7255C15.0775 15.7795 15.7489 15.066 15.7489 14.25V11.958C15.7489 11.2956 15.3144 10.7116 14.6799 10.5213L12.6435 9.91035C12.1149 9.75179 11.542 9.89623 11.1518 10.2864L10.5901 10.8482C9.15291 10.0389 7.95998 8.84599 7.15074 7.40881L7.71246 6.84709C8.10266 6.45689 8.24711 5.88396 8.08854 5.35541L7.47761 3.31898C7.28727 2.6845 6.70329 2.25 6.04087 2.25H3.7489Z"></path>
                    </svg>
                  ) : (
                    <svg height="1em" width="1em" viewBox="0 0 300.000000 300.000000" xmlns="http://www.w3.org/2000/svg" fill="none">
                      <g transform="translate(0.000000,300.000000) scale(0.100000,-0.100000)" stroke="white" stroke-width="150">
                        <path d="M1329 2760 c-530 -61 -984 -407 -1142 -870 -46 -135 -62 -233 -61 -385 0 -206 37 -357 139 -566 25 -51 45 -98 45 -105 0 -7 -43 -129 -95 -272 -52 -143 -95 -269 -95 -280 0 -23 30 -52 55 -52 10 0 135 45 279 100 144 55 268 100 277 100 8 0 58 -21 110 -46 110 -54 259 -105 379 -131 121 -26 449 -26 570 0 509 109 894 438 1038 887 17 52 36 133 42 179 17 115 8 342 -19 448 -123 492 -530 865 -1063 973 -114 24 -343 33 -459 20z m421 -56 c188 -31 383 -109 555 -222 101 -67 272 -240 340 -345 144 -223 202 -432 192 -694 -6 -175 -36 -295 -111 -449 -153 -311 -429 -543 -782 -657 -166 -54 -297 -71 -489 -64 -226 8 -372 44 -580 140 -76 36 -122 51 -150 51 -26 0 -125 -33 -279 -93 -131 -51 -248 -91 -260 -89 -15 2 -20 9 -18 23 3 17 113 330 158 448 24 65 18 115 -27 208 -58 118 -86 195 -110 302 -103 462 100 949 509 1221 311 207 669 282 1052 220z" />
                        <path d="M725 1758 c-80 -30 -154 -106 -175 -179 -63 -220 162 -420 365 -325 91 42 165 153 165 247 0 85 -73 200 -154 241 -48 25 -156 33 -201 16z m186 -55 c91 -47 141 -144 124 -243 -17 -102 -123 -190 -227 -190 -200 0 -303 243 -165 389 71 75 173 91 268 44z" />
                        <path d="M1395 1746 c-58 -27 -108 -74 -138 -131 -17 -31 -22 -58 -22 -115 0 -84 19 -130 77 -189 128 -131 341 -95 431 74 30 56 30 174 0 230 -50 94 -146 155 -243 155 -37 0 -72 -8 -105 -24z m179 -28 c83 -25 156 -126 156 -218 0 -122 -108 -230 -230 -230 -61 0 -114 23 -160 70 -94 93 -94 226 0 320 65 66 143 85 234 58z" />
                        <path d="M2120 1761 c-111 -36 -200 -153 -200 -261 0 -93 74 -204 165 -246 179 -84 388 64 373 264 -7 98 -66 185 -153 226 -43 21 -146 30 -185 17z m158 -47 c20 -8 52 -29 71 -47 149 -142 48 -397 -157 -397 -104 0 -210 88 -227 190 -29 173 153 321 313 254z" />
                      </g>
                    </svg>
                  )}
                </div>
              </button>
            )}
          </div>
        </div>
      </>
    )
  }

  return (
    <div className={`chat-widget-container ${isOpen ? "bubble-open" : ""}`}>
      <div className='chat-widget-call-btn'>
        <button
          className="widget-float-button"
          onClick={toggleWidget}
          aria-label="Open chat widget"
        >
          <div className="button-content">
            <div className="call-button">
              <div className='icon-arrow'>
                <slot name="icon-chevron-up" aria-hidden="true" class="flex text-lg transition-[margin] duration-200 -mx-0.5 transition-transform duration-200 -rotate-180">
                  <svg xmlns="http://www.w3.org/2000/svg" height="1em" width="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m18 15-6-6-6 6"></path></svg></slot>
              </div>
              <div className='icon-phone'>
                {isVoice ? (
                  <svg height="1em" width="1em" viewBox="0 0 18 18" fill="currentColor" xmlns="http://www.w3.org/2000/svg"><path d="M3.7489 2.25C2.93286 2.25 2.21942 2.92142 2.27338 3.7963C2.6686 10.2041 7.79483 15.3303 14.2026 15.7255C15.0775 15.7795 15.7489 15.066 15.7489 14.25V11.958C15.7489 11.2956 15.3144 10.7116 14.6799 10.5213L12.6435 9.91035C12.1149 9.75179 11.542 9.89623 11.1518 10.2864L10.5901 10.8482C9.15291 10.0389 7.95998 8.84599 7.15074 7.40881L7.71246 6.84709C8.10266 6.45689 8.24711 5.88396 8.08854 5.35541L7.47761 3.31898C7.28727 2.6845 6.70329 2.25 6.04087 2.25H3.7489Z"></path></svg>
                ) : (
                  <svg height="1em" width="1em" viewBox="0 0 300.000000 300.000000" xmlns="http://www.w3.org/2000/svg" fill="none">
                    <g transform="translate(0.000000,300.000000) scale(0.100000,-0.100000)" stroke="white" stroke-width="170">
                      <path d="M1329 2760 c-530 -61 -984 -407 -1142 -870 -46 -135 -62 -233 -61 -385 0 -206 37 -357 139 -566 25 -51 45 -98 45 -105 0 -7 -43 -129 -95 -272 -52 -143 -95 -269 -95 -280 0 -23 30 -52 55 -52 10 0 135 45 279 100 144 55 268 100 277 100 8 0 58 -21 110 -46 110 -54 259 -105 379 -131 121 -26 449 -26 570 0 509 109 894 438 1038 887 17 52 36 133 42 179 17 115 8 342 -19 448 -123 492 -530 865 -1063 973 -114 24 -343 33 -459 20z m421 -56 c188 -31 383 -109 555 -222 101 -67 272 -240 340 -345 144 -223 202 -432 192 -694 -6 -175 -36 -295 -111 -449 -153 -311 -429 -543 -782 -657 -166 -54 -297 -71 -489 -64 -226 8 -372 44 -580 140 -76 36 -122 51 -150 51 -26 0 -125 -33 -279 -93 -131 -51 -248 -91 -260 -89 -15 2 -20 9 -18 23 3 17 113 330 158 448 24 65 18 115 -27 208 -58 118 -86 195 -110 302 -103 462 100 949 509 1221 311 207 669 282 1052 220z" />
                      <path d="M725 1758 c-80 -30 -154 -106 -175 -179 -63 -220 162 -420 365 -325 91 42 165 153 165 247 0 85 -73 200 -154 241 -48 25 -156 33 -201 16z m186 -55 c91 -47 141 -144 124 -243 -17 -102 -123 -190 -227 -190 -200 0 -303 243 -165 389 71 75 173 91 268 44z" />
                      <path d="M1395 1746 c-58 -27 -108 -74 -138 -131 -17 -31 -22 -58 -22 -115 0 -84 19 -130 77 -189 128 -131 341 -95 431 74 30 56 30 174 0 230 -50 94 -146 155 -243 155 -37 0 -72 -8 -105 -24z m179 -28 c83 -25 156 -126 156 -218 0 -122 -108 -230 -230 -230 -61 0 -114 23 -160 70 -94 93 -94 226 0 320 65 66 143 85 234 58z" />
                      <path d="M2120 1761 c-111 -36 -200 -153 -200 -261 0 -93 74 -204 165 -246 179 -84 388 64 373 264 -7 98 -66 185 -153 226 -43 21 -146 30 -185 17z m158 -47 c20 -8 52 -29 71 -47 149 -142 48 -397 -157 -397 -104 0 -210 88 -227 190 -29 173 153 321 313 254z" />
                    </g>
                  </svg>
                )}
              </div>
              <span className='start-call'>Support</span>
            </div>
          </div>
        </button>
      </div>

      {isOpen && (
        <div className="widget-panel">
          <div className="top-bar" style={{ background: themeColor }}>
            <button onClick={toggleWidget} className="icon-btn">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                <path d="M15 18l-6-6 6-6" />
              </svg>
            </button>

            <span className="title">AgentZena</span>

            <button className="icon-btn" onClick={toggleWidget}>
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
          <div className={`widget-panel-middle-scroll-area ${"chat-area-zena"}`}>{renderChat()}</div>
        </div>
      )}
    </div>
  );
}

function ChatWidget({
  agentId = AGENT_ID,
  domain = DOMAIN,
  themeColor = THEME_COLOR,
  accessToken = ACCESS_TOKEN
}) {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isVoice, setIsVoice] = useState(false);

  useEffect(() => {
    axios.post('https://backend.ormeagent.com/api/suggestedQuestions/getByStoreUrl', {
      storeUrl: domain
    })
      .then((res) => {
        setQuestions(res?.data?.data)
        setLoading(false)
        setIsVoice(res?.data?.voice)
      })
  }, [])

  const renderWidget = () => {
    if (loading) {
      return (
        <div className={`chat-widget-container`}>
          <div className='chat-widget-call-btn'>
            <button
              className="widget-float-button"
              disabled
              aria-label="Open chat widget"
            >
              <div className="button-content">
                <div className="call-button">
                  <div className='icon-phone'>
                    <svg height="1em" width="1em" viewBox="0 0 50 50" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="25" cy="25" r="20" stroke="currentColor" stroke-width="5" fill="none" stroke-dasharray="31.4159265358979" stroke-dashoffset="0">
                        <animate
                          attributeName="stroke-dashoffset"
                          from="0"
                          to="31.4159265358979"
                          dur="1.5s"
                          repeatCount="indefinite"
                          keyTimes="0;1" />
                      </circle>
                    </svg>
                  </div>
                  <span className='start-call'>. . . .</span>
                </div>
              </div>
            </button>
          </div>
        </div>
      )
    } else {
      return (
        <InnerComponent
          agentId={agentId}
          domain={domain}
          themeColor={themeColor}
          accessToken={accessToken}
          questions={questions}
          isVoice={isVoice}
        />
      )
    }
  }

  return (
    <ShadowWrapper themeColor={themeColor}>
      {renderWidget()}
    </ShadowWrapper>
  )
}

export default ChatWidget;
